/**
 * Drives API
 * CRUD operations for drives/motors (Motoren)
 */

import {handleSupabaseError, supabase} from './client'
import type {Drive, ListDrive} from '@/types/Drive'
import {AssetFetchParams, AssetFetchResult} from "@/types/Asset";
import {getRowsFiltered, ListParams, ListResponse} from "@/api/index";

export type DrivePosition = Pick<Drive, 'asset_id' | 'location_row' | 'location_col' | 'location_level'>

/** Entfernt den in Aufgaben verwendeten SPM-Praefix an genau einer zentralen Stelle. */
export function normalizeAssetId(assetId: string): string {
    return assetId.trim().replace(/^spm/i, '').trim()
}

/** Liefert Existenz und Position eines Assets mit einer einzigen Maybe-Query. */
export async function getDrivePosition(assetId: string): Promise<DrivePosition | null> {
    const normalized = normalizeAssetId(assetId)
    if (!normalized) return null

    const {data, error} = await supabase
        .from('drives')
        .select('asset_id, location_row, location_col, location_level')
        .eq('asset_id', normalized)
        .maybeSingle()

    if (error) handleSupabaseError(error)
    return data as DrivePosition | null
}

/**
 * Get single drive by asset ID
 */
export async function getDrive(assetId: string): Promise<Drive | null> {
    const {data, error} = await supabase
        .from('drives')
        .select('*')
        .eq('asset_id', assetId)
        .single()

    if (error) {
        if (error.code === 'PGRST116') return null
        handleSupabaseError(error)
    }

    return data
}

/**
 * Get all drives
 */
export async function getDrives(): Promise<Drive[]> {
    const {data, error} = await supabase
        .from('drives')
        .select('*')
        .order('asset_id', {ascending: true})

    if (error) handleSupabaseError(error)
    return data || []
}

/*export async function getDrivesFiltered(params: ListParams): Promise<ListResponse<ListDrive>> {
    const {
        page = 1,
        itemsPerPage = 10,
        sortBy = 'asset_id',
        sortOrder = 'ASC',
        filters = {}
    } = params

    // Query mit Count starten
    let query = supabase
        .from('drives')
        .select('asset_id, description_primary, type_comment, is_safety, location_row, location_col, location_level ,system_power, system_power_unit', {count: 'exact'})

    // Filter anwenden
    for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null && value !== '') {
            query = applyFilter(query, key, String(value))
        }
    }

    // Sortierung
    if (sortBy) {
        query = query.order(sortBy, {ascending: sortOrder?.toUpperCase() !== 'DESC'})
    }

    // Pagination
    const from = (page - 1) * itemsPerPage
    const to = from + itemsPerPage - 1
    query = query.range(from, to)

    const {data, error, count} = await query

    if (error) throw error

    return {
        items: data || [],
        total: count || 0
    }
}*/

export async function getDrivesFiltered(params: ListParams): Promise<ListResponse<ListDrive>> {
    //asset_id, description_primary, type_comment, is_safety, location_row, location_col, location_level ,system_power, system_power_unit
    return getRowsFiltered(params, "drives", "*")
}

/*
* TODO: prependIcon
* */
export async function getCompressedDrives(params: AssetFetchParams): Promise<AssetFetchResult> {
    const {
        offset = 0,
        limit = 100,
        search = ""
    } = params;

    const {data, error} = await supabase.rpc('filter_rows', {
        p_table: "drives",
        p_filters: {},
        p_sort_column: 'asset_id',
        p_sort_order: 'asc',
        p_limit: limit,
        p_offset: offset,
        p_select: "asset_id, description_primary, location_row, location_col, location_level, is_safety",
        p_search: search,
        p_search_columns: ['asset_id', 'description_primary']
    })

    if (error) throw error

    return {
        items: (data?.items || []).map((d: any) => ({
            title: d.asset_id,
            subtitle: d.description_primary || '',
            chip: `${d.location_row}${d.location_col} ${d.location_level}`,
            is_safety: d.is_safety,
            asset_id: d.asset_id,
        })),
        total: data?.total || 0,
    }
}


/**
 * Get drives for search/autocomplete
 */
export async function getDrivesList(): Promise<{ asset_id: string; description_primary: string }[]> {
    const {data, error} = await supabase
        .from('drives')
        .select('asset_id, description_primary')
        .order('asset_id', {ascending: true})

    if (error) handleSupabaseError(error)
    return data || []
}

/**
 * Create new drive
 */
export async function createDrive(drive: Partial<Drive>): Promise<Drive> {
    const {data, error} = await supabase
        .from('drives')
        .insert(drive)
        .select()
        .single()

    if (error) handleSupabaseError(error)
    return data
}

/**
 * Update drive
 */
export async function updateDrive(assetId: string, updates: Partial<Drive>): Promise<Drive> {
    const {data, error} = await supabase
        .from('drives')
        .update(updates)
        .eq('asset_id', assetId)
        .select()
        .single()

    if (error) handleSupabaseError(error)
    return data
}

/**
 * Delete drive
 */
export async function deleteDrive(assetId: string): Promise<void> {
    const {error} = await supabase
        .from('drives')
        .delete()
        .eq('asset_id', assetId)

    if (error) handleSupabaseError(error)
}
